import { ReactNode, useState } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import {
  BREAKPOINTS_WIDTHS,
  useIsClientReady,
  useWindowSize,
} from '@belong/common';
import { IconMenu, IconClose } from '@belong/icons';
import { ButtonBase, Image, LinkBase } from '@belong/ui';
import clsx from 'clsx';
import { useRegions } from 'src/features/app';
import { useModal } from 'src/hooks/useModal';
import type { DesktopNavigationProps } from './desktop-navigation/desktop-navigation';
import css from './header.module.css';
import { MarketBanner } from './market-banner/market-banner';
import { MobileNavigationProps } from './mobile-navigation/mobile-navigation';

type HeaderVariant = 'primary' | 'secondary' | 'tertiary';

type HeaderProps = {
  regionSelector?: ReactNode;
  variant?: HeaderVariant;
  theme?: 'light' | 'dark';
  isFullWidth?: boolean;
};

export function Header({
  regionSelector: RegionSelector,
  variant = 'primary',
  theme = 'light',
  isFullWidth = false,
}: HeaderProps): JSX.Element {
  const [isOverlayOpen, openOverlayModal, closeOverlayModal] = useModal();
  const [isMobileNavLoaded, setIsMobileNavLoaded] = useState(false);
  const { width } = useWindowSize();
  const { pathname, asPath } = useRouter();
  const isClientReady = useIsClientReady();
  const { regions } = useRegions();

  const isMobile = width < BREAKPOINTS_WIDTHS.LG;
  /**
   * Where the market banner shows.
   *
   * The three fixed paths are known at build time; the region pages are not —
   * `useRegions` fetches them, so on the server that list is empty and on the
   * client it fills in. Held behind `isClientReady` for that reason, and it is
   * a real bug rather than caution: React compares its first client render
   * against the server's HTML, and a region page rendered the banner on one
   * side and not the other, which is a whole element appearing out of nowhere
   * — "Hydration failed because the initial UI does not match what was
   * rendered on the server". The banner then arrives a frame later on those
   * pages, which is what already happened; it just no longer takes the page
   * down with it.
   *
   * The fixed paths stay outside the gate on purpose, so the homepage keeps
   * the banner in its HTML and does not shift as it hydrates.
   */
  const bannerUrls = [
    '/',
    '/homeowners',
    '/homeowners/leasing',
    ...(isClientReady
      ? regions.map((region) => `/homeowners/${region.nameKebabCase}`)
      : []),
  ];

  function handleOverlayOpen() {
    // After mobile nav is opened for the first time, rely only on the isOverlayOpen prop for opening and closing.
    if (!isMobileNavLoaded) {
      setIsMobileNavLoaded(true);
    }

    openOverlayModal();
  }

  return (
    <>
      {(bannerUrls.includes(pathname) || bannerUrls.includes(asPath)) &&
        !isOverlayOpen && <MarketBanner />}
      <header
        className={clsx(
          'duration-hover transition-colors',
          variantToBgClassNameMap[variant],
          'h-header relative z-10 shrink-0',
          {
            // z-20 keeps the header (and its close button) above the mobile
            // navigation sheet, which sits at z-10.
            'bg-white z-20': isOverlayOpen,
          },
        )}
      >
        <div
          className={clsx(
            'container relative flex h-full items-center justify-between ',
            {
              ['w-full px-lg']: isFullWidth,
            },
          )}
        >
          {(!isMobile || !isOverlayOpen) && (
            <div className="flex items-center">
              <div className="mr-lg xl:mr-xl flex">
                <LinkBase
                  href="/"
                  className={css.logoLink}
                  aria-label="Go To Homepage"
                >
                  <Image
                    src={
                      theme === 'dark'
                        ? '/logos/belong_logo_web_white.svg'
                        : variantToLogoSrcMap[variant]
                    }
                    alt="belong logo"
                    aria-hidden="true"
                  />
                </LinkBase>
              </div>
              {RegionSelector}
            </div>
          )}

          {isClientReady && (
            <div className="ml-auto flex">
              {isMobile ? (
                <div className="z-20 flex items-end lg:hidden">
                  {isOverlayOpen ? (
                    <ButtonBase onClick={closeOverlayModal}>
                      <IconClose
                        width={16}
                        className={
                          isOverlayOpen
                            ? 'text-navy'
                            : variantToClassNameMap[variant]
                        }
                      />
                    </ButtonBase>
                  ) : (
                    <ButtonBase
                      onClick={handleOverlayOpen}
                      aria-label="Burger Menu"
                    >
                      <IconMenu
                        width={24}
                        height={18}
                        aria-hidden="true"
                        className={clsx(
                          variantToClassNameMap[variant],
                          'text-white',
                        )}
                      />
                    </ButtonBase>
                  )}
                  {(isOverlayOpen || isMobileNavLoaded) && (
                    <MobileNavigation
                      isOverlayOpen={isOverlayOpen}
                      openOverlayModal={openOverlayModal}
                      closeOverlayModal={closeOverlayModal}
                    />
                  )}
                </div>
              ) : (
                <div className="hidden lg:flex">
                  <DesktopNavigation theme={theme} />
                </div>
              )}
            </div>
          )}
        </div>
      </header>
    </>
  );
}

const DesktopNavigation = dynamic<DesktopNavigationProps>(
  () =>
    import('./desktop-navigation/desktop-navigation').then(
      (mod) => mod.DesktopNavigation,
    ),
  { ssr: false },
);

const MobileNavigation = dynamic<MobileNavigationProps>(
  () =>
    import('./mobile-navigation/mobile-navigation').then(
      (mod) => mod.MobileNavigation,
    ),
  { ssr: false },
);

const variantToClassNameMap: Record<HeaderVariant, string> = {
  primary: '',
  secondary: 'text-white',
  tertiary: '',
};

const variantToBgClassNameMap: Record<HeaderVariant, string> = {
  primary: 'bg-white',
  secondary: 'bg-transparent',
  tertiary: 'bg-transparent',
};

const variantToLogoSrcMap: Record<HeaderVariant, string> = {
  primary: '/logos/belong_logo_web.svg',
  secondary: '/logos/belong_logo_web_white.svg',
  tertiary: '/logos/belong_logo_web.svg',
};
